<component-modal [showClose]="true">
  <ng-container title> Profile </ng-container>

  <div body>
    <component-tabs [tabs]="tabs" class="mb-0" [activeTab]="activeTab()" (tabChange)="onTabChange($event)" />

    <!-- Profile Tab -->
    @if (activeTab().id === "profile") {
      <!-- Profile Avatar -->
      <div class="d-flex align-items-center py-3">
        <div class="profile-avatar-wrapper" (mouseenter)="isHoveringAvatar.set(true)" (mouseleave)="isHoveringAvatar.set(false)">
          @if (hasAvatar()) {
            <img [src]="user().avatar" alt="Avatar" class="profile-avatar-img rounded-circle" />
          } @else {
            <div class="profile-avatar rounded-circle">
              <span class="avatar-letter">{{ avatarLetter() }}</span>
            </div>
          }

          <!-- Hover Overlay -->
          @if (isHoveringAvatar() && !isUploadingAvatar()) {
            <div class="avatar-overlay rounded-circle">
              @if (!hasAvatar()) {
                <button type="button" class="avatar-action-btn rounded-circle" (click)="onAvatarClick()" title="Upload avatar">
                  <custom-icon name="upload" [size]="20" viewBox="0 0 20 20" />
                </button>
              } @else {
                <div class="avatar-actions">
                  <button type="button" class="avatar-action-btn rounded-circle" (click)="onAvatarClick()" title="Change avatar">
                    <custom-icon name="refresh" [size]="20" viewBox="0 0 20 20" />
                  </button>
                  <button type="button" class="avatar-action-btn rounded-circle" (click)="onDeleteAvatar()" title="Delete avatar">
                    <custom-icon name="trashBin" [size]="20" viewBox="0 0 18 18" />
                  </button>
                </div>
              }
            </div>
          }

          <!-- Loading Spinner -->
          @if (isUploadingAvatar()) {
            <div class="avatar-overlay rounded-circle">
              <div class="spinner-border text-light" role="status">
                <span class="visually-hidden">Uploading...</span>
              </div>
            </div>
          }
        </div>

        <div class="flex-grow-1 ps-3">
          @if (user().organization?.plan; as plan) {
            <div class="d-flex align-items-center mb-2">
              <span class="fw-bold me-2">{{ plan.name }}</span>
              <span class="text-muted">Usage This Period:</span>
            </div>
            <div class="p-3 rounded-3 plan-limit-card">
              @if (plan.tokensLimit) {
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span class="small">Tokens Used</span>
                  <span class="small fw-bold"
                    >{{ usage()?.tokensUsedSinceReset || 0 | number }} / {{ plan.tokensLimit | number }}</span
                  >
                </div>
                <div class="progress" style="height: 5px">
                  <div
                    class="progress-bar"
                    role="progressbar"
                    [style.width.%]="((usage()?.tokensUsedSinceReset || 0) / plan.tokensLimit) * 100"
                    aria-valuenow="((usage()?.tokensUsed || 0) / plan.tokensLimit) * 100"
                    aria-valuemin="0"
                    aria-valuemax="100"></div>
                </div>
              } @else if (plan.aiRequestsLimit) {
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span class="small">AI Requests</span>
                  <span class="small fw-bold"
                    >{{ usage()?.aiRequestsUsedSinceReset || 0 | number }} / {{ plan.aiRequestsLimit | number }}</span
                  >
                </div>
                <div class="progress" style="height: 5px">
                  <div
                    class="progress-bar"
                    role="progressbar"
                    [style.width.%]="((usage()?.aiRequestsUsedSinceReset || 0) / plan.aiRequestsLimit) * 100"
                    aria-valuenow="((usage()?.aiRequestsUsed || 0) / plan.aiRequestsLimit) * 100"
                    aria-valuemin="0"
                    aria-valuemax="100"></div>
                </div>
              }
            </div>
          } @else {
            <p>You are not currently on a plan.</p>
          }
        </div>
      </div>

      <!-- Email (read-only) -->
      <div class="mb-3">
        <component-input
          type="email"
          label="Email"
          size="lg"
          [value]="email()"
          [disabled]="true"
          [floating]="false"
          [secondary]="true" />
      </div>

      <!-- First Name and Last Name Row -->
      <div class="row mb-3">
        <div class="col-md-6">
          <component-input
            type="text"
            label="First name"
            [value]="firstName()"
            size="lg"
            [floating]="false"
            (valueChange)="firstName.set($event!)" />
        </div>
        <div class="col-md-6">
          <component-input
            type="text"
            label="Last name"
            [value]="lastName()"
            size="lg"
            [floating]="false"
            (valueChange)="lastName.set($event!)" />
        </div>
      </div>

      <!-- Error message -->
      @if (errorMessage()) {
        <div class="alert alert-danger mt-3">
          {{ errorMessage() }}
        </div>
      }

      <!-- Password Section - Only for LOCAL auth provider -->
      @if (isLocalAuth()) {
        <div class="mt-4 pt-3 border-top">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h6 class="mb-1">Password</h6>
              <p class="text-muted small mb-0">Set a new password to enhance your security.</p>
            </div>
            <button type="button" class="btn btn-primary action-btn" (click)="onChangePassword()">Change</button>
          </div>
        </div>
      }

      <!-- Delete Account Section -->
      @if (!user().isTemporary) {
        <div class="mt-4 pt-3 border-top">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h6 class="mb-1">Delete account</h6>
              <p class="text-muted small mb-0">Delete your account permanently from our system.</p>
            </div>
            <button type="button" class="btn btn-secondary danger action-btn" (click)="onDeleteAccount()">Delete</button>
          </div>
        </div>
      }
    }

    <!-- Settings Tab -->
    @if (activeTab().id === "settings") {
      <div class="settings-section">
        <!-- Autosave Toggle -->
        <div class="setting-item">
          <div class="setting-info">
            <h6 class="setting-title">Autosave</h6>
            <p class="setting-description">Save your progress automatically as you write.</p>
          </div>
          <component-toggle [checked]="true" [disabled]="true" />
        </div>

        <!-- Version History Toggle -->
        <div class="setting-item">
          <div class="setting-info">
            <h6 class="setting-title">Version History</h6>
            <p class="setting-description">Keep a history of document changes.</p>
          </div>
          <component-toggle [checked]="true" [disabled]="true" />
        </div>

        <!-- Track Changes Toggle -->
        <div class="setting-item">
          <div class="setting-info">
            <h6 class="setting-title">Track Changes</h6>
            <p class="setting-description">Highlight edits and suggestions in real time.</p>
          </div>
          <component-toggle [checked]="true" [disabled]="true" />
        </div>

        <!-- Default AI Model -->
        <div class="setting-item">
          <div class="setting-info">
            <h6 class="setting-title">Default AI Model</h6>
            <p class="setting-description">Select the default AI model for new documents.</p>
          </div>
          <div class="setting-control">
            <component-ai-model-selector [selectedModelId]="defaultAiModel()" (selectedModelIdChange)="onAiModelChange($event)" />
          </div>
        </div>

        <!-- Help Me Create AI Model -->
        <div class="setting-item">
          <div class="setting-info">
            <h6 class="setting-title">Help Me Create AI Model</h6>
            <p class="setting-description">Select the default AI model for generating role instructions.</p>
          </div>
          <div class="setting-control">
            <component-ai-model-selector
              [selectedModelId]="helpMeCreateAiModel()"
              (selectedModelIdChange)="onHelpMeCreateAiModelChange($event)" />
          </div>
        </div>

        <!-- Error message -->
        @if (errorMessage()) {
          <div class="alert alert-danger mt-3">
            {{ errorMessage() }}
          </div>
        }
      </div>
    }
  </div>

  <!-- Footer only visible on Profile tab -->
  @if (activeTab().id === "profile") {
    <div footer class="grid-auto-cols flex-grow-1 gap-3">
      <button type="button" class="btn btn-secondary" (click)="activeModal.close()" [disabled]="isSaving()">Cancel</button>
      <button type="button" class="btn btn-primary" (click)="onSave()" [disabled]="isSaving()">
        @if (isSaving()) {
          <span class="spinner-border spinner-border-sm me-2"></span>
        }
        Save changes
      </button>
    </div>
  }
</component-modal>
